import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/PageHeader";
import { ListsContent } from "./ListsContent";
import { ListsFilterWrapper } from "./ListsFilterWrapper";
import { ListsActions } from "./ListsActions";
import { CreateListForm } from "./CreateListForm";
import { useLists, useCreateList } from "@/hooks/lists/useLists";
import { ListsFilterProvider } from "@/contexts/ListsFilterContext";
import { usePersistedViewMode } from "@/utils/viewMode";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const ListsPage = () => {
  const { t } = useTranslation();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [viewMode, setViewMode] = usePersistedViewMode("lists");
  
  const { data: lists = [], isLoading, error } = useLists();
  const createListMutation = useCreateList();

  const handleCreateList = async (name: string, description?: string) => {
    await createListMutation.mutateAsync({ name, description });
    setIsCreateDialogOpen(false);
  };

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">
            {t("common.error", "Error")}
          </h2>
          <p className="text-gray-600">
            {t("lists.error.loading", "Failed to load lists. Please try again.")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <ListsFilterProvider viewMode={viewMode} setViewMode={setViewMode}>
      <div>
        <PageHeader
          title={t("lists.header", "Lists")}
          actions={
            <ListsActions
              onCreateList={() => setIsCreateDialogOpen(true)}
            />
          }
        />
        
        <ListsFilterWrapper />
        
        <ListsContent lists={lists} isLoading={isLoading} />
        
        <CreateListForm
          isOpen={isCreateDialogOpen}
          onClose={() => setIsCreateDialogOpen(false)}
          onSubmit={handleCreateList}
          isLoading={createListMutation.isPending}
        />
      </div>
    </ListsFilterProvider>
  );
};
